// #3185 APPROVE.2 PR 2c — the client approval matrix as the web shows it.
//
// Mirrors apps/api/src/services/clientApprovalRules.service.ts
// (CLIENT_APPROVABLE_TYPES_BY_PHASE, PLATFORM_DEFAULT_REQUIRED_TYPES). The
// workspace and project screens take the type list from the API's `types`
// field and only use this for grouping and labels, so a type the API adds
// still renders (under "Other"). The Partner Portal matrix has no GET that
// lists types, so it uses this list; step3a's validator refuses anything else.

import { ARTEFACT_TYPE_LABELS } from '../utils/artefactDisplayName';

export interface ClientApprovalPhase {
  key: string;
  label: string;
  types: readonly string[];
}

// The prototype sits in Requirements for projects using the Prototype Agent,
// in UX Design otherwise; at workspace level it is listed under UX Design.
export const CLIENT_APPROVAL_PHASES: readonly ClientApprovalPhase[] = [
  { key: 'p1',   label: 'Requirements',    types: ['prd', 'user_stories'] },
  { key: 'p2',   label: 'Architecture',    types: ['sad', 'adr', 'erd'] },
  { key: 'p2_5', label: 'UX Design',       types: ['user_journey_map', 'screen_inventory', 'screen_spec', 'prototype'] },
  { key: 'p3',   label: 'Sprint Planning', types: ['sprint_plan'] },
  { key: 'p6',   label: 'QA',              types: ['qa_report', 'test_plan'] },
  { key: 'p7',   label: 'Deployment',      types: ['deployment_runbook'] },
  { key: 'p8',   label: 'Retrospective',   types: ['retro_report'] },
];

export const CLIENT_APPROVABLE_TYPES: readonly string[] = CLIENT_APPROVAL_PHASES.flatMap((p) => p.types);

/** Required by the platform default, when the project has a client stakeholder (ruling D9). */
export const PLATFORM_DEFAULT_REQUIRED_TYPES: readonly string[] = ['prd', 'user_stories'];

export const platformDefaultRequired = (type: string) => PLATFORM_DEFAULT_REQUIRED_TYPES.includes(type);

export function clientApprovalTypeLabel(type: string): string {
  if (type === 'prototype') return 'Prototype';
  return ARTEFACT_TYPE_LABELS[type] ?? type;
}

export const typeNote = (type: string): string | null =>
  type === 'prototype' ? 'Requirements phase when the project uses the Prototype Agent.' : null;

/** Group `types` (as the API lists them) by phase; unknown types go last, under "Other". */
export function groupByPhase(types: readonly string[]): ClientApprovalPhase[] {
  const groups = CLIENT_APPROVAL_PHASES
    .map((p) => ({ ...p, types: p.types.filter((t) => types.includes(t)) }))
    .filter((p) => p.types.length > 0);
  const known = new Set(CLIENT_APPROVABLE_TYPES);
  const other = types.filter((t) => !known.has(t));
  return other.length > 0 ? [...groups, { key: 'other', label: 'Other', types: other }] : groups;
}

export const requiredLabel = (required: boolean) => (required ? 'Required' : 'Not required');

/** Ruling D9: the workspace and platform defaults apply only to projects with a client stakeholder. */
export const D9_COPY =
  'Defaults apply only to projects that have a client stakeholder. A project without one needs no client approval unless its own override says so.';
